const textEncoder = new TextEncoder()

function ascii(bytes, offset, length = 4) {
  return String.fromCharCode(...bytes.subarray(offset, offset + length))
}

function writeAscii(target, offset, text) {
  for (let i = 0; i < text.length; i += 1) target[offset + i] = text.charCodeAt(i)
}

function readUint32LE(bytes, offset) {
  return (bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] << 24)) >>> 0
}

function writeUint16LE(bytes, offset, value) {
  bytes[offset] = value & 0xff
  bytes[offset + 1] = value >>> 8 & 0xff
}

function writeUint24LE(bytes, offset, value) {
  bytes[offset] = value & 0xff
  bytes[offset + 1] = value >>> 8 & 0xff
  bytes[offset + 2] = value >>> 16 & 0xff
}

function writeUint32LE(bytes, offset, value) {
  bytes[offset] = value & 0xff
  bytes[offset + 1] = value >>> 8 & 0xff
  bytes[offset + 2] = value >>> 16 & 0xff
  bytes[offset + 3] = value >>> 24 & 0xff
}

function makeChunk(fourcc, payload) {
  const paddedLength = payload.length + (payload.length & 1)
  const chunk = new Uint8Array(8 + paddedLength)
  writeAscii(chunk, 0, fourcc)
  writeUint32LE(chunk, 4, payload.length)
  chunk.set(payload, 8)
  return chunk
}

function extractFrameChunks(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer)
  if (bytes.length < 20 || ascii(bytes, 0) !== "RIFF" || ascii(bytes, 8) !== "WEBP") {
    throw new Error("Browser returned an invalid still WebP frame.")
  }
  const chunks = []
  let hasImage = false
  for (let offset = 12; offset + 8 <= bytes.length;) {
    const tag = ascii(bytes, offset)
    const size = readUint32LE(bytes, offset + 4)
    const end = offset + 8 + size
    if (end > bytes.length) throw new Error("Still WebP frame has a truncated RIFF chunk.")
    if (tag === "ALPH" || tag === "VP8 " || tag === "VP8L") {
      const payload = bytes.slice(offset + 8, end)
      chunks.push(makeChunk(tag, payload))
      if (tag === "VP8 " || tag === "VP8L") hasImage = true
    }
    offset = end + (size & 1)
  }
  if (!hasImage) throw new Error("Still WebP frame contains no VP8 or VP8L image chunk.")
  return chunks
}

function concat(parts) {
  const length = parts.reduce((sum, part) => sum + part.length, 0)
  const output = new Uint8Array(length)
  let offset = 0
  for (const part of parts) {
    output.set(part, offset)
    offset += part.length
  }
  return output
}

function makeVp8x(width, height) {
  const payload = new Uint8Array(10)
  payload[0] = 0x02 // Animation flag.
  writeUint24LE(payload, 4, width - 1)
  writeUint24LE(payload, 7, height - 1)
  return makeChunk("VP8X", payload)
}

function makeAnim() {
  const payload = new Uint8Array(6)
  payload.set([0, 0, 0, 255], 0) // Opaque black in BGRA byte order.
  writeUint16LE(payload, 4, 0) // Infinite loop.
  return makeChunk("ANIM", payload)
}

function makeAnmf(width, height, duration, stillWebp) {
  const frameChunks = extractFrameChunks(stillWebp)
  const frameData = concat(frameChunks)
  const payload = new Uint8Array(16 + frameData.length)
  writeUint24LE(payload, 0, 0)
  writeUint24LE(payload, 3, 0)
  writeUint24LE(payload, 6, width - 1)
  writeUint24LE(payload, 9, height - 1)
  writeUint24LE(payload, 12, Math.max(10, Math.min(0xffffff, Math.round(duration))))
  payload[15] = 0x02 // Full-canvas frame: do not blend, do not dispose.
  payload.set(frameData, 16)
  return makeChunk("ANMF", payload)
}

export function muxAnimatedWebp(width, height, frames) {
  if (!Number.isInteger(width) || !Number.isInteger(height) || width < 1 || height < 1) {
    throw new Error("Animated WebP dimensions are invalid.")
  }
  if (!Array.isArray(frames) || !frames.length) throw new Error("Animated WebP has no frames.")
  const chunks = [makeVp8x(width, height), makeAnim()]
  for (const frame of frames) chunks.push(makeAnmf(width, height, frame.duration, frame.webp))
  const body = concat([textEncoder.encode("WEBP"), ...chunks])
  const output = new Uint8Array(8 + body.length)
  writeAscii(output, 0, "RIFF")
  writeUint32LE(output, 4, body.length)
  output.set(body, 8)
  return output
}

export function inspectAnimatedWebp(buffer) {
  const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer)
  if (ascii(bytes, 0) !== "RIFF" || ascii(bytes, 8) !== "WEBP") return { valid: false, frames: 0, loopCount: null }
  let frames = 0
  let loopCount = null
  let animationFlag = false
  for (let offset = 12; offset + 8 <= bytes.length;) {
    const tag = ascii(bytes, offset)
    const size = readUint32LE(bytes, offset + 4)
    if (tag === "VP8X" && size >= 1) animationFlag = Boolean(bytes[offset + 8] & 0x02)
    if (tag === "ANIM" && size >= 6) loopCount = bytes[offset + 12] | bytes[offset + 13] << 8
    if (tag === "ANMF") frames += 1
    offset += 8 + size + (size & 1)
  }
  return { valid: animationFlag && frames > 0, frames, loopCount }
}
